import { useState, useEffect } from "react";
import { 
  Calendar, Clock, User, Users, Phone, Mail, CreditCard, Package,
  CheckCircle, XCircle, AlertCircle, Search, Filter, Eye, 
  ChevronDown, RefreshCw, Wallet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SmsCell, SmsDetailPanel } from "@/components/admin/SmsStatus";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", color: "bg-yellow-500/20 text-yellow-500", icon: AlertCircle },
  confirmed: { label: "Bestätigt", color: "bg-green-500/20 text-green-500", icon: CheckCircle },
  rejected: { label: "Abgelehnt", color: "bg-red-500/20 text-red-500", icon: XCircle },
  cancelled: { label: "Storniert", color: "bg-red-500/20 text-red-500", icon: XCircle },
  completed: { label: "Abgeschlossen", color: "bg-blue-500/20 text-blue-500", icon: CheckCircle },
  no_show: { label: "Nicht erschienen", color: "bg-gray-500/20 text-gray-500", icon: XCircle },
};

const PAYMENT_STATUS_CONFIG = {
  pending: { label: "Ausstehend", color: "bg-yellow-500/20 text-yellow-500" },
  paid: { label: "Bezahlt", color: "bg-green-500/20 text-green-500" },
  credit_used: { label: "Guthaben", color: "bg-blue-500/20 text-blue-500" },
  refunded: { label: "Erstattet", color: "bg-purple-500/20 text-purple-500" },
};

export default function TermineTab({ adminToken }) {
  const [activeSubTab, setActiveSubTab] = useState("termine");
  const [appointments, setAppointments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState({});
  const [packageStats, setPackageStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (activeSubTab === "termine") {
      fetchAppointments();
    } else {
      fetchPackages();
    }
  }, [activeSubTab, adminToken]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/all`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setAppointments(data.appointments || []);
      setStats(data.stats || {});
    } catch (err) {
      toast.error("Fehler beim Laden der Termine");
    }
    setLoading(false);
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/packages/all`, {
        headers: { "X-Admin-Token": adminToken }
      });
      const data = await res.json();
      setPackages(data.packages || []);
      setPackageStats(data.stats || {});
    } catch (err) {
      toast.error("Fehler beim Laden der Pakete");
    }
    setLoading(false);
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/${appointmentId}/status`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken 
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success("Status aktualisiert");
        fetchAppointments();
        setShowModal(false);
      } else {
        toast.error("Fehler beim Aktualisieren");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const updatePaymentStatus = async (appointmentId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/${appointmentId}/payment`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken 
        },
        body: JSON.stringify({ payment_status: newStatus })
      });
      
      if (res.ok) {
        toast.success("Zahlungsstatus aktualisiert");
        fetchAppointments();
      } else {
        toast.error("Fehler beim Aktualisieren");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/${appointmentId}/confirm`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.already_confirmed) {
          toast.info("Termin wurde bereits bestätigt");
        } else {
          toast.success("Termin bestätigt! Rechnung erstellt und E-Mail gesendet.");
        }
        fetchAppointments();
        setShowModal(false);
      } else {
        toast.error(data.detail || "Fehler beim Bestätigen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const rejectAppointment = async (appointmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/${appointmentId}/reject`, {
        method: "POST",
        headers: { "X-Admin-Token": adminToken }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.already_rejected) {
          toast.info("Termin wurde bereits abgelehnt");
        } else {
          toast.success("Termin abgelehnt. Absage-E-Mail gesendet.");
        }
        fetchAppointments();
        setShowModal(false);
      } else {
        toast.error(data.detail || "Fehler beim Ablehnen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  const activatePackage = async (packageId) => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/admin/packages/${packageId}/activate`, {
        method: "PATCH",
        headers: { "X-Admin-Token": adminToken }
      });
      
      if (res.ok) {
        toast.success("Paket aktiviert");
        fetchPackages();
        setShowModal(false);
      } else {
        toast.error("Fehler beim Aktivieren");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  // Filter appointments
  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = searchTerm === "" || 
      apt.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.appointment_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || apt.status === statusFilter;
    const matchesPayment = paymentFilter === "all" || apt.payment_status === paymentFilter;
    
    return matchesSearch && matchesStatus && matchesPayment;
  });

  // Filter packages
  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = searchTerm === "" || 
      pkg.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.package_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || pkg.status === statusFilter;
    const matchesPayment = paymentFilter === "all" || pkg.payment_status === paymentFilter;
    
    return matchesSearch && matchesStatus && matchesPayment;
  });

  return (
    <div className="space-y-6" data-testid="termine-tab">
      {/* Sub-Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-4">
        <Button
          variant={activeSubTab === "termine" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("termine")}
          className={activeSubTab === "termine" ? "bg-[#c1ff72] text-[#0A0A0A]" : ""}
        >
          <Calendar className="w-4 h-4 mr-2" />
          Termine
        </Button>
        <Button
          variant={activeSubTab === "pakete" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("pakete")}
          className={activeSubTab === "pakete" ? "bg-[#c1ff72] text-[#0A0A0A]" : ""}
        >
          <Package className="w-4 h-4 mr-2" />
          Kundenpakete
        </Button>
      </div>

      {/* Termine Tab */}
      {activeSubTab === "termine" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
              <p className="text-2xl font-bold text-[#c1ff72]">{stats.total || 0}</p>
              <p className="text-sm text-[#71717A]">Gesamt</p>
            </div>
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-yellow-500/30">
              <p className="text-2xl font-bold text-yellow-500">{stats.pending || 0}</p>
              <p className="text-sm text-[#71717A]">Ausstehend</p>
            </div>
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-green-500/30">
              <p className="text-2xl font-bold text-green-500">{stats.confirmed || 0}</p>
              <p className="text-sm text-[#71717A]">Bestätigt</p>
            </div>
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-blue-500/30">
              <p className="text-2xl font-bold text-blue-500">{stats.completed || 0}</p>
              <p className="text-sm text-[#71717A]">Abgeschlossen</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                placeholder="Suche nach Name, E-Mail oder ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#1A1A1A] border-white/10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-[#1A1A1A] border-white/10">
                <SelectValue placeholder="Status Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="confirmed">Bestätigt</SelectItem>
                <SelectItem value="completed">Abgeschlossen</SelectItem>
                <SelectItem value="cancelled">Storniert</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-[180px] bg-[#1A1A1A] border-white/10">
                <SelectValue placeholder="Zahlung Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Zahlungen</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="paid">Bezahlt</SelectItem>
                <SelectItem value="credit_used">Guthaben</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAppointments}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Appointments Table */}
          <div className="bg-[#1A1A1A] rounded-lg border border-white/10 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-[#71717A]">Lade Termine...</div>
            ) : filteredAppointments.length === 0 ? (
              <div className="p-8 text-center text-[#71717A]">Keine Termine gefunden</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4 font-medium text-[#71717A]">Termin</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Kunde</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Training</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Status</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Zahlung</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Preis</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">SMS</th>
                      <th className="text-right p-4 font-medium text-[#71717A]">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((apt) => {
                      const statusConfig = STATUS_CONFIG[apt.status] || STATUS_CONFIG.pending;
                      const paymentConfig = PAYMENT_STATUS_CONFIG[apt.payment_status] || PAYMENT_STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={apt.appointment_id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="p-4">
                            <div className="font-medium">{apt.date}</div>
                            <div className="text-xs text-[#71717A]">{apt.time} - {apt.end_time}</div>
                            <div className="text-xs text-[#c1ff72]">{apt.duration || 60} Min</div>
                          </td>
                          <td className="p-4">
                            <div className="font-medium">{apt.customer?.name}</div>
                            <div className="text-xs text-[#71717A]">{apt.customer?.email}</div>
                          </td>
                          <td className="p-4">
                            <div>{apt.training_name}</div>
                            {apt.player_count > 1 && (
                              <div className="text-xs text-[#71717A]">{apt.player_count} Spieler</div>
                            )}
                            {apt.training_location && (
                              <div className="text-xs text-[#71717A] truncate max-w-[150px]" title={apt.training_location}>
                                📍 {apt.training_location}
                              </div>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${paymentConfig.color}`}>
                              {paymentConfig.label}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-[#c1ff72] font-semibold">{apt.total_price}€</span>
                          </td>
                          <td className="p-4">
                            <SmsCell apt={apt} />
                          </td>
                          <td className="p-4 text-right space-x-1">
                            {/* Show Confirm/Reject buttons for PENDING appointments */}
                            {apt.status === "pending" && (
                              <>
                                <Button 
                                  variant="default" 
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  onClick={() => confirmAppointment(apt.appointment_id)}
                                  data-testid={`btn-confirm-${apt.appointment_id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Bestätigen
                                </Button>
                                <Button 
                                  variant="default" 
                                  size="sm"
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                  onClick={() => rejectAppointment(apt.appointment_id)}
                                  data-testid={`btn-reject-${apt.appointment_id}`}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Ablehnen
                                </Button>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedItem(apt); setShowModal(true); }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Pakete Tab */}
      {activeSubTab === "pakete" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
              <p className="text-2xl font-bold text-[#c1ff72]">{packageStats.total || 0}</p>
              <p className="text-sm text-[#71717A]">Pakete Gesamt</p>
            </div>
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-green-500/30">
              <p className="text-2xl font-bold text-green-500">{packageStats.active || 0}</p>
              <p className="text-sm text-[#71717A]">Aktiv</p>
            </div>
            <div className="bg-[#1A1A1A] p-4 rounded-lg border border-yellow-500/30">
              <p className="text-2xl font-bold text-yellow-500">{packageStats.pending || 0}</p>
              <p className="text-sm text-[#71717A]">Ausstehend</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                placeholder="Suche nach Name, E-Mail oder ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#1A1A1A] border-white/10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-[#1A1A1A] border-white/10">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchPackages}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Packages Table */}
          <div className="bg-[#1A1A1A] rounded-lg border border-white/10 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-[#71717A]">Lade Pakete...</div>
            ) : filteredPackages.length === 0 ? (
              <div className="p-8 text-center text-[#71717A]">Keine Pakete gefunden</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4 font-medium text-[#71717A]">Kunde</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Paket</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Training</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Guthaben</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Status</th>
                      <th className="text-left p-4 font-medium text-[#71717A]">Preis</th>
                      <th className="text-right p-4 font-medium text-[#71717A]">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPackages.map((pkg) => (
                      <tr key={pkg.package_id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <div className="font-medium">{pkg.customer_name}</div>
                          <div className="text-xs text-[#71717A]">{pkg.customer_email}</div>
                        </td>
                        <td className="p-4">
                          <div className="font-medium">{pkg.package_name}</div>
                          <div className="text-xs text-[#71717A] font-mono">{pkg.package_id}</div>
                        </td>
                        <td className="p-4">{pkg.training_name}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-[#c1ff72]" />
                            <span className="font-semibold">{pkg.remaining_credits}</span>
                            <span className="text-[#71717A]">/ {pkg.total_credits}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-1.5 mt-1">
                            <div 
                              className="bg-[#c1ff72] h-1.5 rounded-full" 
                              style={{ width: `${(pkg.remaining_credits / pkg.total_credits) * 100}%` }}
                            />
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            pkg.status === "active" 
                              ? "bg-green-500/20 text-green-500" 
                              : "bg-yellow-500/20 text-yellow-500"
                          }`}>
                            {pkg.status === "active" ? "Aktiv" : "Ausstehend"}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-[#c1ff72] font-semibold">{pkg.total_price}€</span>
                        </td>
                        <td className="p-4 text-right space-x-2">
                          {pkg.status === "pending" && (
                            <Button 
                              variant="default" 
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => activatePackage(pkg.package_id)}
                            >
                              Aktivieren
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedItem(pkg); setShowModal(true); }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeSubTab === "termine" ? "Termindetails" : "Paketdetails"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && activeSubTab === "termine" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#71717A]">Termin-ID</p>
                  <p className="font-mono">{selectedItem.appointment_id}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Datum & Zeit</p>
                  <p>{selectedItem.date} • {selectedItem.time}-{selectedItem.end_time}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Trainingsdauer</p>
                  <p>{selectedItem.duration || 60} Minuten</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Training</p>
                  <p>{selectedItem.training_name}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Spieler</p>
                  <p>{selectedItem.player_count}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Preis</p>
                  <p className="text-[#c1ff72] font-semibold">{selectedItem.total_price}€</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Kunde</p>
                  <p>{selectedItem.customer?.name}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Zahlungsart</p>
                  <p>{selectedItem.payment_method === 'cash' ? 'Barzahlung' : selectedItem.payment_method === 'bank_transfer' ? 'Überweisung' : selectedItem.payment_method === 'paypal' ? 'PayPal' : selectedItem.payment_method === 'training_card' ? `Trainingskarte ${selectedItem.card?.card_id || ''}` : selectedItem.payment_method}</p>
                </div>
              </div>
              
              {selectedItem.training_location && (
                <div className="bg-white/5 p-3 rounded-lg text-sm">
                  <p className="text-[#71717A] mb-1">📍 Trainingsort</p>
                  <p>{selectedItem.training_location}</p>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-[#71717A]" />
                <a href={`mailto:${selectedItem.customer?.email}`} className="text-[#c1ff72] hover:underline">
                  {selectedItem.customer?.email}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-[#71717A]" />
                <a href={`tel:${selectedItem.customer?.phone}`} className="hover:text-[#c1ff72]">
                  {selectedItem.customer?.phone}
                </a>
              </div>

              {selectedItem.message && (
                <div>
                  <p className="text-[#71717A] text-sm mb-1">Nachricht</p>
                  <p className="text-sm bg-white/5 p-3 rounded-lg">{selectedItem.message}</p>
                </div>
              )}

              <SmsDetailPanel
                apt={selectedItem}
                adminToken={adminToken}
                onChanged={async () => { setShowModal(false); await fetchAppointments(); }}
              />

              {/* Confirm/Reject Buttons for PENDING appointments */}
              {selectedItem.status === "pending" && (
                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm text-[#71717A] mb-3">Termin bestätigen oder ablehnen:</p>
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      onClick={() => confirmAppointment(selectedItem.appointment_id)}
                      data-testid="modal-btn-confirm"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Bestätigen
                    </Button>
                    <Button 
                      className="flex-1 bg-red-500 hover:bg-red-600"
                      onClick={() => rejectAppointment(selectedItem.appointment_id)}
                      data-testid="modal-btn-reject"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Ablehnen
                    </Button>
                  </div>
                  <p className="text-xs text-[#71717A] mt-2">
                    Bei Bestätigung wird automatisch eine Rechnung erstellt und per E-Mail mit PDF-Anhang gesendet.
                  </p>
                </div>
              )}

              {/* Status info for confirmed/rejected appointments */}
              {selectedItem.status === "confirmed" && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-green-500 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Termin bestätigt
                  </p>
                  {selectedItem.invoice_number && (
                    <p className="text-xs text-[#71717A] mt-1">Rechnung: {selectedItem.invoice_number}</p>
                  )}
                </div>
              )}

              {selectedItem.status === "rejected" && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-500 font-semibold flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Termin abgelehnt
                  </p>
                </div>
              )}

              <div className="border-t border-white/10 pt-4 space-y-3">
                <div>
                  <Label className="text-[#71717A]">Status ändern</Label>
                  <Select 
                    value={selectedItem.status} 
                    onValueChange={(v) => updateAppointmentStatus(selectedItem.appointment_id, v)}
                  >
                    <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Ausstehend</SelectItem>
                      <SelectItem value="confirmed">Bestätigt</SelectItem>
                      <SelectItem value="rejected">Abgelehnt</SelectItem>
                      <SelectItem value="completed">Abgeschlossen</SelectItem>
                      <SelectItem value="cancelled">Storniert</SelectItem>
                      <SelectItem value="no_show">Nicht erschienen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedItem.payment_status !== "credit_used" && (
                  <div>
                    <Label className="text-[#71717A]">Zahlungsstatus</Label>
                    <Select 
                      value={selectedItem.payment_status} 
                      onValueChange={(v) => updatePaymentStatus(selectedItem.appointment_id, v)}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-white/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Ausstehend</SelectItem>
                        <SelectItem value="paid">Bezahlt</SelectItem>
                        <SelectItem value="refunded">Erstattet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedItem && activeSubTab === "pakete" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#71717A]">Paket-ID</p>
                  <p className="font-mono">{selectedItem.package_id}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Paket</p>
                  <p>{selectedItem.package_name}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Training</p>
                  <p>{selectedItem.training_name}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Preis</p>
                  <p className="text-[#c1ff72] font-semibold">{selectedItem.total_price}€</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Kunde</p>
                  <p>{selectedItem.customer_name}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Guthaben</p>
                  <p>{selectedItem.remaining_credits} / {selectedItem.total_credits}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-[#71717A]" />
                <span>{selectedItem.customer_email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-[#71717A]" />
                <span>{selectedItem.customer_phone}</span>
              </div>

              {selectedItem.status === "pending" && (
                <Button 
                  className="w-full bg-green-500 hover:bg-green-600"
                  onClick={() => activatePackage(selectedItem.package_id)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Paket aktivieren (Zahlung eingegangen)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
